import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import PageHero from '../components/PageHero';
import { postList } from '../data/posts';
import './Blogs.css';

const EMAIL = 'eatsleepcodereapeat@gmail.com';

const Blogs = () => {
  const [category, setCategory] = useState('All');
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const categories = useMemo(() => ['All', ...new Set(postList.map((post) => post.category))], []);
  const filtered = category === 'All' ? postList : postList.filter((post) => post.category === category);
  const [lead, ...rest] = filtered;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'ESCR LLC Blog',
    description: 'Insights, Tutorials, and News from the World of Software Development',
    url: 'https://escr.online/blogs',
    publisher: {
      '@type': 'Organization',
      name: 'ESCR LLC',
      logo: {
        '@type': 'ImageObject',
        url: 'https://escr.online/logo.png'
      }
    },
    blogPost: postList.map(post => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      url: `https://escr.online/blogs/${post.id}`
    }))
  };

  // There is no newsletter backend yet, so subscribing opens a pre-filled email.
  const handleSubscribe = (e) => {
    e.preventDefault();
    const body = `Please subscribe ${newsletterEmail} to the ESCR newsletter.`;
    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent('Newsletter subscription')}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="blogs">
      <SEO
        title="Blog"
        description="Read our latest articles, tutorials, and insights about software development, web development, cloud computing, and technology trends."
        keywords="software development blog, web development articles, technology blog, programming tutorials, software engineering"
        structuredData={structuredData}
      />

      <PageHero
        eyebrow="Journal"
        title="Notes from the codebase."
        subtitle="Insights, tutorials, and news from the world of software development."
      />

      <section className="section section-tight">
        <div className="container">
          <div className="blog-filters" role="group" aria-label="Filter posts by category">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`blog-filter ${category === cat ? 'is-active' : ''}`}
                aria-pressed={category === cat}
                onClick={() => setCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {lead && (
            <Reveal key={`lead-${lead.id}`}>
              <Link to={`/blogs/${lead.id}`} className="card blog-lead" aria-label={`Read article: ${lead.title}`}>
                <div className="blog-lead-art" aria-hidden="true">
                  <span className="blog-lead-code">
                    {'<'}<b>article</b> <i>id</i>=<em>"{lead.id}"</em>{' />'}
                  </span>
                </div>
                <div className="blog-lead-body">
                  <div className="blog-meta">
                    <span className="tag tag-accent">{lead.category}</span>
                    <span className="blog-meta-text">{lead.date}</span>
                    <span className="blog-meta-text">{lead.readTime}</span>
                  </div>
                  <h2 className="blog-lead-title">{lead.title}</h2>
                  <p className="blog-lead-excerpt">{lead.excerpt}</p>
                  <span className="link-arrow">
                    Read article <Icon name="arrowRight" />
                  </span>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="blog-grid">
            {rest.map((post, i) => (
              <Reveal key={post.id} delay={(i % 3) * 80}>
                <Link to={`/blogs/${post.id}`} className="card blog-card" aria-label={`Read article: ${post.title}`}>
                  <div className="blog-meta">
                    <span className="tag">{post.category}</span>
                    <span className="blog-meta-text">{post.readTime}</span>
                  </div>
                  <h2 className="blog-card-title">{post.title}</h2>
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                  <div className="blog-card-footer">
                    <span className="blog-meta-text">{post.date}</span>
                    <Icon name="arrowUpRight" className="blog-card-arrow" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="section section-tight">
        <div className="container">
          <Reveal className="newsletter">
            <div>
              <span className="eyebrow">Newsletter</span>
              <h2 className="newsletter-title">Stay updated</h2>
              <p className="newsletter-desc">
                Subscribe to receive the latest articles, tutorials, and updates directly in your inbox.
              </p>
            </div>
            <form className="newsletter-form" onSubmit={handleSubscribe} aria-label="Newsletter subscription">
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                placeholder="you@company.com"
                className="newsletter-input"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary">
                Subscribe
                <Icon name="arrowRight" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Blogs;
